/* eslint-disable dot-notation */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui/icons
import Email from '@material-ui/icons/Email';
// @material-ui/core components
import Icon from '@material-ui/core/Icon';
import InputAdornment from '@material-ui/core/InputAdornment';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import CardHeader from 'components/UI/Card/CardHeader';
import CardFooter from 'components/UI/Card/CardFooter';
import Button from 'components/UI/CustomButtons/Button';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import CustomInput from 'components/UI/CustomInput/CustomInput';

/**
 * Validation form's rules
 */
const validationSchema = Yup.object({
  username: Yup.string('Este campo debe de tener valor').required(
    'Email or password is required',
  ),
  password: Yup.string('Este campo debe de tener valor').required(
    'Enter your password',
  ),
});

/**
 * @class
 * @extends React.Component
 */
class UserLogin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      username: '',
      password: '',
      cardAnimation: 'cardHidden',
    };
    this.formikRef = null;
  }

  /**
   * @description
   * React lifecycle method called when component is mounted.
   * Handle timeOutFunction for transition
   */
  componentDidMount() {
    this.timeOutFunction = setTimeout(() => {
      this.setState({ cardAnimation: '' });
    }, 500);
  }

  /**
   * @description
   * React lifecycle method called when component is unmounted.
   * Restart timeOutFunction for transition
   */
  componentWillUnmount() {
    clearTimeout(this.timeOutFunction);
    this.timeOutFunction = null;
  }

  /**
   * @description
   * Set server errors with Formik setErrors function.
   *
   * @param {Array} serverErrors - Api errors.
   * @returns {Object} Global form's errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Render Login form using Formik.
   */
  render() {
    const { username, password, cardAnimation } = this.state;
    const { classes, login, errors: serverErrors, t } = this.props;

    const all = this.setServerErrors(serverErrors);

    return (
      <React.Fragment>
        <Snackbars place="tc" message={all || ''} open={!!all} color="danger" />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={{ username, password }}
          validationSchema={validationSchema}
          onSubmit={values => {
            login(values);
          }}
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
          }) => (
            <GridItem className={classes.cardContainer}>
              <form onSubmit={handleSubmit}>
                <Card login className={classes[cardAnimation]}>
                  <CardHeader
                    className={`${classes.cardHeader} ${classes.textCenter}`}
                    color="primary"
                  >
                    <h4 className={classes.cardTitle}>{t('user.login.btn')}</h4>
                  </CardHeader>
                  <CardBody>
                    <CustomInput
                      labelText={t('user.login.username')}
                      id="username"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={touched.username && Boolean(errors.username)}
                      helpText={touched.username ? errors.username : ''}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Email className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                        value: values.username,
                        onChange: e => {
                          handleChange(e);
                        },
                        onBlur: handleBlur,
                        type: 'text',
                      }}
                    />
                    <CustomInput
                      labelText={t('common.pass.title')}
                      id="password"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={touched.password && Boolean(errors.password)}
                      helpText={touched.password ? errors.password : ''}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Icon className={classes.inputAdornmentIcon}>
                              lock_outline
                            </Icon>
                          </InputAdornment>
                        ),
                        value: values.password,
                        onChange: e => {
                          handleChange(e);
                        },
                        onBlur: handleBlur,
                        type: 'password',
                      }}
                    />
                  </CardBody>
                  <CardFooter className={classes.justifyContentCenter}>
                    <Button
                      id="user-add-button-save"
                      type="submit"
                      color="twitter"
                      simple
                      size="lg"
                      block
                    >
                      {t('user.login.btn')}
                    </Button>
                  </CardFooter>
                </Card>
              </form>
            </GridItem>
          )}
        />
      </React.Fragment>
    );
  }
}

/**
 * UserLogin PropTypes
 * @param classes - prop styles received from withStyles
 * @param errors - prop errors list received from parents
 * @param t - prop from HOC with string translations
 * @callback login - callback called when validations are success
 */

UserLogin.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  login: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(UserLogin);
